import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, ChevronDown, FileText, Check, Loader2 } from 'lucide-react';
import PractitionerWorksheet from './PractitionerWorksheet';
import useCoachingResources from '../hooks/useCoachingResources';
import { quizTitle, quizSummary } from '../data/quizTitles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '');
const suggested = (r, q) => quizSummary(q).toLowerCase().includes(r.title.toLowerCase());

const ResourcePicker = ({ q, resources, onChange }) => (
  <div className="mt-3" data-testid={`quiz-resources-${q.quiz_id}`}>
    <p className="font-body text-[11px] uppercase tracking-wider text-[#8a6a5a] mb-1.5">Resources to share with this result</p>
    {resources.length === 0 ? (
      <p className="font-body text-xs text-stone-400 italic">Add coaching resources from the practitioner bar first.</p>
    ) : (
      <div className="flex flex-wrap gap-1.5">
        {resources.map((r) => {
          const on = q.resource_ids.includes(r.id);
          return (
            <button
              key={r.id}
              onClick={() => onChange(on ? q.resource_ids.filter((x) => x !== r.id) : [...q.resource_ids, r.id])}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-body border transition-colors ${on ? 'bg-forest text-cream border-forest' : suggested(r, q) ? 'bg-amber-50 text-forest border-amber-300' : 'bg-white text-stone-600 border-stone-200 hover:border-forest/40'}`}
              title={suggested(r, q) && !on ? 'Matches this result' : ''}
              data-testid={`quiz-resource-toggle-${q.quiz_id}-${r.id}`}
            >
              {on && <Check size={11} />} {r.title}{suggested(r, q) && !on ? ' ★' : ''}
            </button>
          );
        })}
      </div>
    )}
  </div>
);

export default function ClientQuizPanel({ auth, clientEmail }) {
  const { resources } = useCoachingResources();
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [worksheet, setWorksheet] = useState(null);
  const creds = { admin_email: auth.email, admin_password: auth.password };

  const load = useCallback(async () => {
    const qs = `admin_email=${encodeURIComponent(auth.email)}&admin_password=${encodeURIComponent(auth.password)}&email=${encodeURIComponent(clientEmail)}`;
    const res = await fetch(`${API}/admin/client-quiz-results?${qs}`).catch(() => null);
    setResults(res && res.ok ? (await res.json()).results : []);
  }, [auth.email, auth.password, clientEmail]);
  useEffect(() => { load(); }, [load]);

  const patch = async (q, path, body, local) => {
    setResults((prev) => prev.map((x) => (x.quiz_id === q.quiz_id ? { ...x, ...local } : x)));
    const res = await fetch(`${API}/admin/quiz-results/${q.quiz_id}/${path}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...creds, ...body }),
    }).catch(() => null);
    if (!res || !res.ok) load();
  };

  const sentCount = (results || []).filter((q) => q.results_sent).length;
  return (
    <div className="mb-6 bg-white border border-[#e2d4bf] rounded-xl" data-testid="client-quiz-panel">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid="client-quiz-panel-toggle">
        <ClipboardList size={16} className="text-[#8a6a5a]" />
        <span className="flex-1 font-heading text-sm text-forest">Quizzes & Assessments</span>
        <span className="font-body text-xs text-stone-500" data-testid="client-quiz-count">
          {results === null ? '…' : results.length === 0 ? 'None yet' : `${results.length} taken · ${sentCount} shared`}
        </span>
        <ChevronDown size={16} className={`text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {results === null && <Loader2 size={16} className="animate-spin text-stone-400" />}
          {results?.length === 0 && <p className="font-body text-xs text-stone-400 italic">This client hasn’t taken any quizzes yet.</p>}
          {results?.map((q) => (
            <div key={q.quiz_id} className="border border-stone-100 rounded-lg p-3" data-testid={`client-quiz-${q.quiz_id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm text-forest font-medium">{quizTitle(q.quiz_type)}</p>
                  <p className="font-body text-xs text-stone-500">{fmt(q.created_at)}{quizSummary(q) ? ` · ${quizSummary(q)}` : ''}</p>
                </div>
                {q.quiz_type !== 'food_preferences' && (
                  <button onClick={() => setWorksheet(q)} className="inline-flex items-center gap-1 text-xs font-body text-forest border border-forest/30 rounded-full px-3 py-1 hover:bg-forest hover:text-cream transition-colors" data-testid={`client-quiz-worksheet-${q.quiz_id}`}>
                    <FileText size={12} /> Complete results
                  </button>
                )}
                <label className="inline-flex items-center gap-1.5 text-xs font-body cursor-pointer" data-testid={`client-quiz-sent-${q.quiz_id}`}>
                  <input
                    type="checkbox"
                    checked={q.results_sent}
                    onChange={(e) => patch(q, 'results-sent', { results_sent: e.target.checked }, { results_sent: e.target.checked })}
                    className="accent-forest"
                  />
                  <span className={q.results_sent ? 'text-emerald-700 font-medium' : 'text-stone-500'}>
                    {q.results_sent ? 'Shared in their journal' : 'Share in their journal'}
                  </span>
                </label>
              </div>
              <ResourcePicker q={q} resources={resources} onChange={(ids) => patch(q, 'resources', { resource_ids: ids }, { resource_ids: ids })} />
            </div>
          ))}
        </div>
      )}
      {worksheet && (
        <PractitionerWorksheet result={worksheet} adminEmail={auth.email} adminPassword={auth.password} onClose={() => setWorksheet(null)} onSaved={load} />
      )}
    </div>
  );
}
